import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { insertPilotApplicationSchema } from "@shared/schema";
import {
  Building2, Users, MapPin, Heart, Briefcase,
  CheckCircle2, ArrowLeft, Send, Factory, Stethoscope
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";

const pilotFormSchema = insertPilotApplicationSchema.extend({
  companyName: z.string().min(1, "Company name is required"),
  applicantName: z.string().min(1, "Your name is required"),
  email: z.string().email("Please enter a valid email"),
  companyType: z.string().min(1, "Please select your company type"),
});
type PilotFormValues = z.infer<typeof pilotFormSchema>;

export default function PilotApplyPage() {
  usePageTitle("Apply for Pilot Program", "Apply to join the HealthWallet MN pilot — free for qualifying small businesses in the Twin Cities.");
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<PilotFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(pilotFormSchema),
    defaultValues: {
      companyName: "", applicantName: "", email: "", phone: "",
      companyType: "", employeeCount: "", location: "", interestArea: "", message: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: PilotFormValues) => {
      await apiRequest("POST", "/api/public/pilot-application", data);
    },
    onSuccess: () => setSubmitted(true),
    onError: () => {
      toast({ title: t('pilot-apply.error-title'), description: t('pilot-apply.error-desc'), variant: "destructive" });
    },
  });

  const handleSubmit = form.handleSubmit((data) => mutation.mutate(data));

  const tiles = [
    { id: "small-biz", icon: Briefcase, label: t('pilot-apply.tile-small-biz-label'), desc: t('pilot-apply.tile-small-biz-desc') },
    { id: "provider", icon: Stethoscope, label: t('pilot-apply.tile-provider-label'), desc: t('pilot-apply.tile-provider-desc') },
    { id: "employer", icon: Factory, label: t('pilot-apply.tile-employer-label'), desc: t('pilot-apply.tile-employer-desc') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(215,50%,12%)] to-[hsl(215,50%,18%)] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6 sm:py-12">
        <div className="flex items-center gap-3 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/pitch")}
            className="text-white/60 hover:text-white"
            data-testid="button-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <img src={healthWalletLogo} alt="HealthWallet" className="w-8 h-8 object-contain" width="1024" height="1024" />
          <span className="text-lg font-bold tracking-wide">HealthWallet</span>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center text-center gap-6 py-16">
            <CheckCircle2 className="w-16 h-16 text-green-400" />
            <h1 className="text-2xl sm:text-3xl font-bold">{t('pilot-apply.success-heading')}</h1>
            <p className="text-white/60 max-w-md">
              {t('pilot-apply.success-body')}
            </p>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                className="border-white/30 text-white bg-white/5"
                onClick={() => navigate("/pitch")}
                data-testid="button-view-pitch"
              >
                {t('pilot-apply.cta-view-pitch')}
              </Button>
              <Button
                variant="outline"
                className="border-white/30 text-white bg-white/5"
                onClick={() => navigate("/")}
                data-testid="button-go-home"
              >
                {t('pilot-apply.cta-go-home')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-[hsl(14,70%,60%)] font-semibold text-sm uppercase tracking-widest mb-3">{t('pilot-apply.tag')}</p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 leading-tight">
                {t('pilot-apply.heading-prefix')}<span className="text-[hsl(14,70%,60%)]">{t('pilot-apply.heading-highlight')}</span>
              </h1>
              <p className="text-white/60 text-base sm:text-lg max-w-2xl">
                {t('pilot-apply.subheading')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {tiles.map((item) => (
                <div key={item.id} className="bg-white/5 border border-white/10 rounded-md p-4 text-center">
                  <item.icon className="w-6 h-6 text-[hsl(14,70%,60%)] mx-auto mb-2" />
                  <h3 className="font-semibold text-sm mb-1">{item.label}</h3>
                  <p className="text-white/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-white/5 border border-white/10 rounded-md p-5 sm:p-8">
              <h2 className="text-lg font-bold mb-6">{t('pilot-apply.form-heading')}</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pilot-company" className="text-white/70 text-sm">{t('pilot-apply.field.organization-name')}</Label>
                    <Input id="pilot-company" {...form.register("companyName")} placeholder={t('pilot-apply.field.organization-name-placeholder')} className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-pilot-company" />
                    {form.formState.errors.companyName && <p className="text-red-400 text-xs mt-1">{t('pilot-apply.validation.company-name-required')}</p>}
                  </div>
                  <div>
                    <Label htmlFor="pilot-name" className="text-white/70 text-sm">{t('pilot-apply.field.contact-name')}</Label>
                    <Input id="pilot-name" {...form.register("applicantName")} placeholder={t('pilot-apply.field.contact-name-placeholder')} className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-pilot-name" />
                    {form.formState.errors.applicantName && <p className="text-red-400 text-xs mt-1">{t('pilot-apply.validation.contact-name-required')}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pilot-email" className="text-white/70 text-sm">{t('pilot-apply.field.email')}</Label>
                    <Input id="pilot-email" type="email" {...form.register("email")} placeholder={t('pilot-apply.field.email-placeholder')} className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-pilot-email" />
                    {form.formState.errors.email && <p className="text-red-400 text-xs mt-1">{t('pilot-apply.validation.email-invalid')}</p>}
                  </div>
                  <div>
                    <Label htmlFor="pilot-phone" className="text-white/70 text-sm">{t('pilot-apply.field.phone')}</Label>
                    <Input id="pilot-phone" {...form.register("phone")} placeholder={t('pilot-apply.field.phone-placeholder')} className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-pilot-phone" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">{t('pilot-apply.field.organization-type')}</Label>
                    <Select
                      value={form.watch("companyType")}
                      onValueChange={(val) => form.setValue("companyType", val, { shouldValidate: true })}
                    >
                      <SelectTrigger className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white" data-testid="select-pilot-type">
                        <SelectValue placeholder={t('pilot-apply.field.organization-type-placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small_business">{t('pilot-apply.company-type.small-business')}</SelectItem>
                        <SelectItem value="healthcare_provider">{t('pilot-apply.company-type.healthcare-provider')}</SelectItem>
                        <SelectItem value="pharmacy">{t('pilot-apply.company-type.pharmacy')}</SelectItem>
                        <SelectItem value="lab">{t('pilot-apply.company-type.lab')}</SelectItem>
                        <SelectItem value="employer">{t('pilot-apply.company-type.employer')}</SelectItem>
                        <SelectItem value="other">{t('pilot-apply.company-type.other')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.formState.errors.companyType && <p className="text-red-400 text-xs mt-1">{t('pilot-apply.validation.company-type-required')}</p>}
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">{t('pilot-apply.field.number-of-employees')}</Label>
                    <Select
                      value={form.watch("employeeCount") || ""}
                      onValueChange={(val) => form.setValue("employeeCount", val)}
                    >
                      <SelectTrigger className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white" data-testid="select-pilot-employees">
                        <SelectValue placeholder={t('pilot-apply.field.number-of-employees-placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="11-25">11-25</SelectItem>
                        <SelectItem value="26-50">26-50</SelectItem>
                        <SelectItem value="51-100">51-100</SelectItem>
                        <SelectItem value="101-500">101-500</SelectItem>
                        <SelectItem value="500+">500+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pilot-location" className="text-white/70 text-sm">{t('pilot-apply.field.location')}</Label>
                    <Input id="pilot-location" {...form.register("location")} placeholder={t('pilot-apply.field.location-placeholder')} className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="input-pilot-location" />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">{t('pilot-apply.field.primary-interest')}</Label>
                    <Select
                      value={form.watch("interestArea") || ""}
                      onValueChange={(val) => form.setValue("interestArea", val)}
                    >
                      <SelectTrigger className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white" data-testid="select-pilot-interest">
                        <SelectValue placeholder={t('pilot-apply.field.primary-interest-placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee_wallets">{t('pilot-apply.interest.employee-wallets')}</SelectItem>
                        <SelectItem value="provider_network">{t('pilot-apply.interest.provider-network')}</SelectItem>
                        <SelectItem value="volume_pricing">{t('pilot-apply.interest.volume-pricing')}</SelectItem>
                        <SelectItem value="payment_infrastructure">{t('pilot-apply.interest.payment-infrastructure')}</SelectItem>
                        <SelectItem value="community_funds">{t('pilot-apply.interest.community-funds')}</SelectItem>
                        <SelectItem value="general">{t('pilot-apply.interest.general')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="pilot-message" className="text-white/70 text-sm">{t('pilot-apply.field.motivation')}</Label>
                  <Textarea
                    id="pilot-message"
                    {...form.register("message")}
                    placeholder={t('pilot-apply.field.motivation-placeholder')}
                    rows={4}
                    className="mt-1 min-h-[44px] bg-white/5 border-white/15 text-white placeholder:text-white/30 resize-none"
                    data-testid="input-pilot-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={mutation.isPending}
                  className="w-full min-h-[44px] bg-[hsl(14,70%,55%)] border border-[hsl(14,70%,65%)] text-white"
                  data-testid="button-submit-pilot"
                >
                  {mutation.isPending ? t('common.cta.submitting') : t('common.cta.submit-application')}
                </Button>
              </form>
            </div>

            <p className="mt-6 text-white/20 text-[10px] text-center leading-relaxed max-w-xl mx-auto">
              {t('pilot-apply.disclaimer')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
